<script setup lang="ts">
  // Per-product sales-over-time stats migrated to the V2 design system.
  // Rows are date × product; the chart draws one series per selected product.
  import { useDebounceFn } from '@vueuse/core';
  import { computed, getCurrentInstance, ref, watch } from 'vue';

  import ItemsService from '@/api/services/items.service';
  import StatsService, {
    type IProductsSeriesStats,
  } from '@/api/services/stats.service';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import V2ReportPage from '@/components/v2/stats/V2ReportPage.vue';
  import V2StatsChartCard from '@/components/v2/stats/V2StatsChartCard.vue';
  import V2StatsDateRange from '@/components/v2/stats/V2StatsDateRange.vue';
  import V2StatsExportButton from '@/components/v2/stats/V2StatsExportButton.vue';
  import V2StatsIntervalSelect from '@/components/v2/stats/V2StatsIntervalSelect.vue';
  import { TStatsChartInterval } from '@/components/v2/stats/v2-stats-interval.types';
  import V2StatsScopeNote from '@/components/v2/stats/V2StatsScopeNote.vue';
  import V2StatsTable from '@/components/v2/stats/V2StatsTable.vue';
  import {
    IV2StatsTableColumn,
    IV2StatsTableRow,
  } from '@/components/v2/stats/v2-stats-table.types';
  import ChartWidget from '@/components/widgets/ChartWidget.vue';
  import { useUi } from '@/composition/use-ui';
  import { IStatsDateRange } from '@/domain/stats/stats-date-range';
  import { IStatsExportColumn } from '@/domain/stats/stats-export';
  import { StatsInterval } from '@/enums/StatsInterval';
  import { AlertType } from '@/interfaces/IAlert';
  import type { IItem } from '@/interfaces/IItem';
  import type { IStats } from '@/interfaces/IStats';
  import type { IStatGroup } from '@/interfaces/IStatGroup';
  import type { IStatItem } from '@/interfaces/IStatItem';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { buildChartLabels, statDateFormatted } from '@/statsutils';
  import {
    formatMenuUsageLabel,
    getEnabledOrderTypesFromBranch,
    getUsedMenuTypesByMenuId,
  } from '@/utils/menu.utils';
  import { displayPrice, initArray, padNumber, round2 } from '@/utils';

  const ui = useUi();
  const restaurantStore = useRestaurantStore();
  const statsService = new StatsService();
  const proxy = getCurrentInstance()!.proxy;

  function i18nT(key: string): string {
    return String(proxy.$t(key));
  }

  const currencySymbol = computed(
    () => restaurantStore.currency?.symbol ?? ''
  );

  const isWhatsappMode = computed(
    () => restaurantStore.selectedBranch?.ordersMode === 'wa'
  );

  //#region Filters

  const range = ref<IStatsDateRange | null>(null);
  const interval = ref<TStatsChartInterval>('auto');
  const chartMetric = ref<'units' | 'total' | 'average'>('units');

  const chartMetricIsCurrency = computed(() => chartMetric.value !== 'units');

  function onRangeChange(value: IStatsDateRange) {
    range.value = value;
    requestLoadStats();
  }

  function setMetric(value: 'units' | 'total' | 'average') {
    chartMetric.value = value;
  }

  //#endregion

  //#region Product selection

  const allItems = ref<IItem[]>([]);
  const itemsLoading = ref(false);
  const selectedItemIds = ref<number[]>([]);

  const restaurantSettings = computed(
    () => restaurantStore.restaurant?.settings ?? null
  );

  const enabledOrderTypes = computed(() =>
    getEnabledOrderTypesFromBranch(restaurantStore.selectedBranch ?? null)
  );

  const usedMenuTypesByMenuId = computed(() =>
    getUsedMenuTypesByMenuId(restaurantSettings.value, enabledOrderTypes.value)
  );

  const shouldFilterByMenu = computed(() => {
    if (!enabledOrderTypes.value.length) return false;
    return usedMenuTypesByMenuId.value.size > 0;
  });

  const showMenuUsage = computed(
    () => shouldFilterByMenu.value && usedMenuTypesByMenuId.value.size > 1
  );

  const usedMenuIds = computed(
    () => new Set(usedMenuTypesByMenuId.value.keys())
  );

  const menuUsageLabelByMenuId = computed(() => {
    const map = new Map<number | null, string>();
    for (const [menuId, types] of usedMenuTypesByMenuId.value.entries()) {
      map.set(menuId, formatMenuUsageLabel(types));
    }
    return map;
  });

  function normalizeMenuId(val: unknown): number | null | undefined {
    if (val === undefined) return undefined;
    if (val === null) return null;
    if (typeof val === 'number' && Number.isFinite(val)) return val;
    return undefined;
  }

  function resolveItemMenuId(item: IItem): number | null | undefined {
    const direct = normalizeMenuId(item.menuId);
    if (direct !== undefined) return direct;

    const fromMenu = normalizeMenuId(item.menu?.id);
    if (fromMenu !== undefined) return fromMenu;

    const fromCategory = normalizeMenuId(item.category?.menuId);
    if (fromCategory !== undefined) return fromCategory;

    const fromCategoryMenu = normalizeMenuId(item.category?.menu?.id);
    if (fromCategoryMenu !== undefined) return fromCategoryMenu;

    return undefined;
  }

  function getItemMenuUsageLabel(item: IItem): string | null {
    if (!showMenuUsage.value) return null;

    const menuId = resolveItemMenuId(item);
    if (menuId === undefined) return null;

    return menuUsageLabelByMenuId.value.get(menuId) ?? null;
  }

  const selectableItems = computed<IItem[]>(() => {
    const items = allItems.value ?? [];
    if (!shouldFilterByMenu.value) return items;

    const used = usedMenuIds.value;
    return items.filter((item) => {
      const menuId = resolveItemMenuId(item);
      if (menuId === undefined) return true;
      return used.has(menuId);
    });
  });

  const allItemIdsSet = computed(() => {
    const set = new Set<number>();
    for (const item of allItems.value ?? []) {
      if (typeof item.id === 'number') set.add(item.id);
    }
    return set;
  });

  const selectableItemIdsSet = computed(() => {
    const set = new Set<number>();
    for (const item of selectableItems.value ?? []) {
      if (typeof item.id === 'number') set.add(item.id);
    }
    return set;
  });

  const sortedSelectableItems = computed<IItem[]>(() => {
    const items = [...(selectableItems.value ?? [])];

    items.sort((a, b) => {
      const nameCmp = (a.name ?? '').localeCompare(b.name ?? '', 'es', {
        sensitivity: 'base',
      });
      if (nameCmp !== 0) return nameCmp;

      if (showMenuUsage.value) {
        const aLabel = getItemMenuUsageLabel(a) ?? '';
        const bLabel = getItemMenuUsageLabel(b) ?? '';
        const labelCmp = aLabel.localeCompare(bLabel, 'es', {
          sensitivity: 'base',
        });
        if (labelCmp !== 0) return labelCmp;
      }

      return (a.id ?? 0) - (b.id ?? 0);
    });

    return items;
  });

  const itemsById = computed(() => {
    const map = new Map<number, IItem>();
    for (const item of sortedSelectableItems.value ?? []) {
      if (typeof item.id === 'number') map.set(item.id, item);
    }
    return map;
  });

  function getItemAutocompleteText(item: IItem): string {
    const label = getItemMenuUsageLabel(item);
    return label ? `${item.name} (${label})` : item.name;
  }

  function getItemAutocompleteSubtitle(item: IItem): string {
    return getItemMenuUsageLabel(item) ?? '';
  }

  function getItemName(itemId: number) {
    const item = itemsById.value.get(itemId) ?? null;
    if (item) return getItemAutocompleteText(item);

    return (
      seriesData.value?.entities?.[itemId]?.name ??
      i18nT('v2Stats.seriesReport.deletedEntity')
    );
  }

  function normalizeSelectedIds(ids: unknown[]): number[] {
    const seen = new Set<number>();
    const out: number[] = [];

    for (let i = ids.length - 1; i >= 0; i--) {
      const raw = Number(ids[i]);
      if (!Number.isFinite(raw) || raw <= 0) continue;
      if (seen.has(raw)) continue;
      seen.add(raw);
      out.push(raw);
    }

    out.reverse();
    return out;
  }

  async function loadItems() {
    if (!restaurantStore.restaurant?.id) {
      allItems.value = [];
      return;
    }

    itemsLoading.value = true;
    try {
      const items = await ItemsService.getAllV2({
        restaurantId: restaurantStore.restaurant.id,
      });

      allItems.value = items;

      if (selectedItemIds.value.length) {
        const availableIds = new Set(items.map((i) => i.id));
        selectedItemIds.value = selectedItemIds.value.filter((id) =>
          availableIds.has(id)
        );
      }
    } catch (error) {
      ui.serverError(error, i18nT('v2Stats.productsSeries.itemsLoadError'));
      allItems.value = [];
    } finally {
      itemsLoading.value = false;
    }
  }

  watch(
    [selectableItemIdsSet, allItemIdsSet, () => itemsLoading.value],
    ([selectableSet, allSet, isLoading]) => {
      if (isLoading) return;
      if (!selectedItemIds.value.length) return;

      const keep: number[] = [];
      let removedByMenu = 0;
      let removedMissing = 0;

      for (const id of selectedItemIds.value) {
        if (selectableSet.has(id)) {
          keep.push(id);
          continue;
        }

        if (allSet.has(id)) removedByMenu += 1;
        else removedMissing += 1;
      }

      if (keep.length === selectedItemIds.value.length) return;

      selectedItemIds.value = keep;

      if (removedByMenu > 0 && removedMissing === 0) {
        ui.showToast(
          AlertType.Info,
          i18nT('v2Stats.productsSeries.hiddenUnusedMenus')
        );
      } else if (removedByMenu === 0 && removedMissing > 0) {
        ui.showToast(
          AlertType.Info,
          i18nT('v2Stats.productsSeries.someUnavailable')
        );
      } else {
        ui.showToast(
          AlertType.Info,
          i18nT('v2Stats.productsSeries.selectionUpdated')
        );
      }
    },
    { immediate: true }
  );

  watch(
    selectedItemIds,
    (val) => {
      const normalized = normalizeSelectedIds(val as unknown[]);

      if (normalized.length > 20) {
        ui.showToast(AlertType.Info, i18nT('v2Stats.productsSeries.maxItems'));
        selectedItemIds.value = normalized.slice(-20);
        return;
      }

      if (
        normalized.length !== val.length ||
        normalized.some((id, index) => id !== val[index])
      ) {
        selectedItemIds.value = normalized;
      }
    },
    { deep: true }
  );

  //#endregion

  //#region Loading

  const loading = ref(false);
  const seriesData = ref<IProductsSeriesStats | null>(null);

  // Guards out-of-order responses on rapid range/selection/interval changes.
  let requestGeneration = 0;

  async function loadStats() {
    const restaurant = restaurantStore.restaurant;
    const branch = restaurantStore.selectedBranch;
    if (!restaurant || !branch || !range.value || isWhatsappMode.value) return;

    if (!selectedItemIds.value.length) {
      requestGeneration++;
      seriesData.value = null;
      return;
    }

    const generation = ++requestGeneration;
    loading.value = true;
    seriesData.value = null;

    try {
      const result = await statsService.getProductsSeries({
        restaurantId: restaurant.id,
        branchId: branch.id,
        from: range.value.from,
        to: range.value.to,
        itemIds: selectedItemIds.value,
        interval: interval.value,
        calendar: range.value.calendar,
      });
      if (generation !== requestGeneration) return;
      seriesData.value = result;
      // Reset series visibility for the freshly loaded selection: index-based
      // toggles must not bleed onto a different set of products.
      statsActive.value = initArray(selectedItemIds.value.length, true);
    } catch (error) {
      if (generation !== requestGeneration) return;
      ui.serverError(error, i18nT('v2Stats.productsSeries.loadError'));
    } finally {
      if (generation === requestGeneration) loading.value = false;
    }
  }

  const requestLoadStats = useDebounceFn(loadStats, 400);

  watch(
    () => restaurantStore.restaurant?.id,
    () => {
      selectedItemIds.value = [];
      seriesData.value = null;
      loadItems().catch(() => {});
    },
    { immediate: true }
  );

  watch([selectedItemIds, interval], () => {
    requestLoadStats();
  });

  //#endregion

  //#region Time buckets

  function toStatsInterval(val: unknown): StatsInterval | null {
    if (val === StatsInterval.Hourly || val === 'hourly')
      return StatsInterval.Hourly;
    if (val === StatsInterval.Daily || val === 'daily')
      return StatsInterval.Daily;
    if (val === StatsInterval.Weekly || val === 'weekly')
      return StatsInterval.Weekly;
    if (val === StatsInterval.Monthly || val === 'monthly')
      return StatsInterval.Monthly;
    return null;
  }

  const effectiveStatsInterval = computed<StatsInterval>(() => {
    return (
      toStatsInterval(seriesData.value?.interval) ??
      toStatsInterval(interval.value) ??
      StatsInterval.Daily
    );
  });

  function toYyyyMMdd(d: Date) {
    return `${padNumber(d.getFullYear(), 4)}${padNumber(
      d.getMonth() + 1,
      2
    )}${padNumber(d.getDate(), 2)}`;
  }

  function listDays(from: Date, to: Date) {
    const result: string[] = [];

    const current = new Date(
      from.getFullYear(),
      from.getMonth(),
      from.getDate()
    );
    const end = new Date(to.getFullYear(), to.getMonth(), to.getDate());

    if (current.getTime() > end.getTime()) return result;

    while (current.getTime() <= end.getTime()) {
      result.push(toYyyyMMdd(current));
      current.setDate(current.getDate() + 1);
    }

    return result;
  }

  function toYyyyMM(d: Date) {
    return `${padNumber(d.getFullYear(), 4)}${padNumber(d.getMonth() + 1, 2)}`;
  }

  function listMonths(from: Date, to: Date) {
    const result: string[] = [];

    const current = new Date(from.getFullYear(), from.getMonth(), 1);
    const end = new Date(to.getFullYear(), to.getMonth(), 1);

    if (current.getTime() > end.getTime()) return result;

    while (current.getTime() <= end.getTime()) {
      result.push(toYyyyMM(current));
      current.setMonth(current.getMonth() + 1);
    }

    return result;
  }

  function startOfWeekMonday(date: Date) {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const day = d.getDay(); // 0 Sunday ... 6 Saturday
    const diff = (day + 6) % 7; // days since Monday
    d.setDate(d.getDate() - diff);
    return d;
  }

  function listWeeks(from: Date, to: Date) {
    const result: string[] = [];

    const start = startOfWeekMonday(from);
    const end = startOfWeekMonday(to);

    if (start.getTime() > end.getTime()) return result;

    const current = new Date(start.getTime());

    while (current.getTime() <= end.getTime()) {
      result.push(toYyyyMMdd(current));
      current.setDate(current.getDate() + 7);
    }

    return result;
  }

  interface ITimeBucket {
    date: string;
    hour?: number;
  }

  const timeBuckets = computed<ITimeBucket[]>(() => {
    if (!range.value) return [];

    const { from, to } = range.value;

    switch (effectiveStatsInterval.value) {
      case StatsInterval.Hourly: {
        const days = listDays(from, to);
        const buckets: ITimeBucket[] = [];

        for (const day of days) {
          for (let hour = 0; hour < 24; hour++) {
            buckets.push({ date: day, hour });
          }
        }

        return buckets;
      }
      case StatsInterval.Daily:
        return listDays(from, to).map((date) => ({ date }));
      case StatsInterval.Weekly:
        return listWeeks(from, to).map((date) => ({ date }));
      case StatsInterval.Monthly:
        return listMonths(from, to).map((date) => ({ date }));
    }
  });

  function getBucketKey(bucket: ITimeBucket) {
    if (effectiveStatsInterval.value === StatsInterval.Hourly) {
      return `${bucket.date}:${bucket.hour ?? 0}`;
    }
    return bucket.date;
  }

  function getBucketSortKey(bucket: ITimeBucket) {
    if (effectiveStatsInterval.value === StatsInterval.Hourly) {
      return `${bucket.date}${padNumber(bucket.hour ?? 0, 2)}`;
    }
    return bucket.date;
  }

  const salesByItemAndBucket = computed(() => {
    const map = new Map<string, { units: number; revenue: number }>();

    for (const p of seriesData.value?.points ?? []) {
      if (!p?.date) continue;

      const itemId = Number(p.itemId);
      if (!Number.isFinite(itemId) || itemId <= 0) continue;

      const date = String(p.date);

      const bucketKey =
        effectiveStatsInterval.value === StatsInterval.Hourly
          ? `${date}:${Number(p.hour ?? 0)}`
          : date;

      const key = `${bucketKey}:${itemId}`;

      const prev = map.get(key) ?? { units: 0, revenue: 0 };
      prev.units += Number(p.units) || 0;
      prev.revenue += Number(p.revenue) || 0;
      map.set(key, prev);
    }

    return map;
  });

  //#endregion

  //#region Chart

  const statsActive = ref<boolean[]>([]);

  const SERIES_COLORS = [
    '#45a5f5',
    '#30d565',
    '#f5e938',
    '#f56041',
    '#f5418a',
    '#c44ff5',
    '#6451f5',
    '#00b8b8',
  ];

  const chartStats = computed<IStats | null>(() => {
    if (!seriesData.value) return null;
    if (!selectedItemIds.value.length) return null;
    if (!timeBuckets.value.length) return null;

    const metric = chartMetric.value;
    const map = salesByItemAndBucket.value;

    const groups: IStatGroup[] = selectedItemIds.value.map((itemId, index) => {
      const stats: IStatItem[] = timeBuckets.value.map((bucket) => {
        const entry = map.get(`${getBucketKey(bucket)}:${itemId}`);
        const units = Number(entry?.units) || 0;
        const revenue = Number(entry?.revenue) || 0;

        let value = 0;
        if (metric === 'units') value = units;
        else if (metric === 'total') value = revenue;
        else value = units > 0 ? revenue / units : 0;

        return { date: bucket.date, hour: bucket.hour, value: round2(value) };
      });

      return {
        id: String(itemId),
        title: getItemName(itemId),
        color: SERIES_COLORS[index % SERIES_COLORS.length],
        type: chartMetricIsCurrency.value ? 'currency' : 'number',
        stats,
      };
    });

    const stats: IStats = {
      interval: effectiveStatsInterval.value,
      labels: [],
      groups,
    };

    stats.labels = buildChartLabels(stats);

    return stats;
  });

  const filteredChartStats = computed<IStats | null>(() => {
    if (!chartStats.value) return null;

    const stats = { ...chartStats.value };
    stats.groups = stats.groups.filter(
      (_value, index) => statsActive.value[index]
    );

    return stats;
  });

  function toggleSeries(index: number) {
    const next = [...statsActive.value];
    next[index] = !next[index];
    if (next.every((active) => !active)) return;
    statsActive.value = next;
  }

  //#endregion

  //#region Table

  const columns = computed<IV2StatsTableColumn[]>(() => {
    const sym = currencySymbol.value;
    return [
      {
        key: 'sortKey',
        label: i18nT('v2Stats.colDate'),
        numeric: false,
        format: (value, row) => String(row.dateLabel ?? ''),
      },
      { key: 'product', label: i18nT('v2Stats.colProduct'), numeric: false },
      {
        key: 'units',
        label: i18nT('v2Stats.productsSeries.colUnits'),
        numeric: true,
      },
      {
        key: 'total',
        label: `${i18nT('v2Stats.series.total')}${sym ? ` (${sym})` : ''}`,
        numeric: true,
        format: (value) => `${sym}${displayPrice(Number(value) || 0)}`,
      },
      {
        key: 'average',
        label: `${i18nT('v2Stats.productsSeries.colAverage')}${
          sym ? ` (${sym})` : ''
        }`,
        numeric: true,
        format: (value) => `${sym}${displayPrice(Number(value) || 0)}`,
      },
    ];
  });

  const rows = computed<IV2StatsTableRow[]>(() => {
    if (!seriesData.value) return [];
    if (!selectedItemIds.value.length) return [];
    if (!timeBuckets.value.length) return [];

    const map = salesByItemAndBucket.value;
    const result: IV2StatsTableRow[] = [];

    for (const bucket of timeBuckets.value) {
      for (const itemId of selectedItemIds.value) {
        const entry = map.get(`${getBucketKey(bucket)}:${itemId}`);
        const units = round2(entry?.units ?? 0);
        const total = round2(entry?.revenue ?? 0);
        const average = units > 0 ? round2(total / units) : 0;

        result.push({
          id: `${getBucketSortKey(bucket)}-${itemId}`,
          sortKey: getBucketSortKey(bucket),
          dateLabel: statDateFormatted({
            date: bucket.date,
            hour: bucket.hour,
            value: units,
          }),
          product: getItemName(itemId),
          units,
          total,
          average,
        });
      }
    }

    return result;
  });

  const exportColumns = computed<IStatsExportColumn[]>(() => [
    { key: 'dateLabel', header: i18nT('v2Stats.colDate') },
    { key: 'product', header: i18nT('v2Stats.colProduct') },
    {
      key: 'units',
      header: i18nT('v2Stats.productsSeries.colUnits'),
      type: 'number',
    },
    { key: 'total', header: i18nT('v2Stats.series.total'), type: 'number' },
    {
      key: 'average',
      header: i18nT('v2Stats.productsSeries.colAverage'),
      type: 'number',
    },
  ]);

  const exportRows = computed<Array<Record<string, unknown>>>(() =>
    rows.value.map((row) => ({ ...row }))
  );

  //#endregion
</script>

<template>
  <V2ReportPage
    :title="String($t('v2Stats.productsSeries.title'))"
    :subtitle="String($t('v2Stats.productsSeries.subtitle'))"
  >
    <template #range>
      <V2StatsIntervalSelect :value="interval" @input="interval = $event" />
      <V2StatsDateRange
        v-if="restaurantStore.restaurant && restaurantStore.selectedBranch"
        :restaurant-id="restaurantStore.restaurant.id"
        :branch-id="restaurantStore.selectedBranch.id"
        storage-key="productsSeries"
        @change="onRangeChange"
      />
    </template>

    <template #note>
      <V2StatsScopeNote
        v-if="!isWhatsappMode"
        storage-key="productsSeries"
        :text="String($t('statsNote.products'))"
      />
    </template>

    <div v-if="isWhatsappMode" class="wa-guard">
      <PosUiEmptyState
        icon="whatsapp"
        :title="String($t('v2Stats.waModeTitle'))"
        :description="String($t('v2Stats.waModeText'))"
      />
    </div>

    <template v-else>
      <div class="series-filters">
        <div class="products-select">
          <v-autocomplete
            v-model="selectedItemIds"
            :items="sortedSelectableItems"
            :item-text="getItemAutocompleteText"
            item-value="id"
            :label="String($t('v2Stats.productsSeries.productsLabel'))"
            :placeholder="
              String($t('v2Stats.productsSeries.productsPlaceholder'))
            "
            multiple
            chips
            deletable-chips
            clearable
            dense
            outlined
            hide-details
            :loading="itemsLoading"
            :disabled="loading"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title v-text="item.name" />
                <v-list-item-subtitle
                  v-if="showMenuUsage && getItemAutocompleteSubtitle(item)"
                  v-text="getItemAutocompleteSubtitle(item)"
                />
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </div>

        <div class="metric-toggle">
          <button
            type="button"
            class="metric-option"
            :class="{ 'is-active': chartMetric === 'units' }"
            @click="setMetric('units')"
          >
            {{ $t('v2Stats.productsSeries.metricUnits') }}
          </button>
          <button
            type="button"
            class="metric-option"
            :class="{ 'is-active': chartMetric === 'total' }"
            @click="setMetric('total')"
          >
            {{ $t('v2Stats.productsSeries.metricTotal') }}
          </button>
          <button
            type="button"
            class="metric-option"
            :class="{ 'is-active': chartMetric === 'average' }"
            @click="setMetric('average')"
          >
            {{ $t('v2Stats.productsSeries.metricAverage') }}
          </button>
        </div>
      </div>

      <div v-if="!selectedItemIds.length" class="select-prompt">
        <PosUiEmptyState
          icon="chart-line"
          :title="String($t('v2Stats.productsSeries.selectPrompt'))"
        />
      </div>

      <template v-else>
        <div
          v-if="chartStats && chartStats.groups.length > 1"
          class="series-toggles"
        >
          <button
            v-for="(group, index) in chartStats.groups"
            :key="group.id"
            type="button"
            class="series-toggle"
            :class="{ 'is-active': statsActive[index] }"
            :style="
              statsActive[index]
                ? {
                    borderColor: group.color,
                    backgroundColor: (group.color || '') + '22',
                  }
                : undefined
            "
            @click="toggleSeries(index)"
          >
            <span
              class="toggle-dot"
              :style="{ backgroundColor: group.color }"
            />
            {{ group.title }}
          </button>
        </div>

        <V2StatsChartCard
          :title="String($t('v2Stats.productsSeries.chartTitle'))"
          :loading="loading"
          :empty="!filteredChartStats || !filteredChartStats.groups.length"
        >
          <template #actions>
            <V2StatsExportButton
              filename="products-series-stats"
              :sheet-name="String($t('v2Stats.productsSeries.title'))"
              :columns="exportColumns"
              :rows="exportRows"
              :disabled="loading"
            />
          </template>
          <ChartWidget
            v-if="filteredChartStats && filteredChartStats.groups.length"
            :stats="filteredChartStats"
            type="line"
            :value-prefix="chartMetricIsCurrency ? currencySymbol : ''"
          />
        </V2StatsChartCard>

        <V2StatsTable
          :columns="columns"
          :rows="rows"
          :loading="loading"
          :default-sort="{ key: 'sortKey', desc: true }"
          sticky-first
        />
      </template>
    </template>
  </V2ReportPage>
</template>

<style scoped lang="scss">
  .wa-guard,
  .select-prompt {
    @apply tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white;
  }

  .series-filters {
    @apply tw-flex tw-flex-wrap tw-items-center tw-justify-between;

    .products-select {
      @apply tw-mb-2 tw-mr-3 tw-w-full tw-max-w-md;
    }

    .metric-toggle {
      @apply tw-mb-2 tw-inline-flex tw-rounded-lg tw-bg-neutral-100 tw-p-0.5;

      .metric-option {
        // h-10 options + track padding = 44px total hit area.
        @apply tw-h-10 tw-whitespace-nowrap tw-rounded-md tw-border-0 tw-bg-transparent tw-px-3 tw-text-sm tw-font-medium tw-text-neutral-500 tw-transition-colors;

        &.is-active {
          @apply tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-text-neutral-900 tw-shadow-sm;
        }

        &:focus-visible {
          @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
        }
      }
    }
  }

  .series-toggles {
    @apply tw-flex tw-flex-wrap tw-items-center;

    .series-toggle {
      // margins instead of flex gap: unsupported on Chrome <84.
      @apply tw-mb-1.5 tw-mr-1.5 tw-inline-flex tw-h-11 tw-items-center tw-whitespace-nowrap tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-px-3 tw-text-sm tw-font-medium tw-text-neutral-500 tw-transition-colors;

      &.is-active {
        @apply tw-text-neutral-900;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }

      .toggle-dot {
        @apply tw-mr-1.5 tw-h-2.5 tw-w-2.5 tw-rounded-full tw-bg-neutral-300;
      }
    }
  }
</style>
